import { useEffect, useState } from 'react';
import type { Profesional } from '../../../api/ops/citas';
import type { Recolocacion, ResolucionRecolocacion } from '../../../api/ops/servicios';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Desplegable } from '../../../componentes/Desplegable';
import { Fecha } from '../../../componentes/Fecha';
import { Icono } from '../../../componentes/Icono';
import type { NombreIcono } from '../../../componentes/iconos';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { Ventana } from '../../../componentes/Ventana';
import { fechaLarga, hoyEn } from '../../../logica/horario';
import type { PropsPantalla } from '../../tipos';
import { mayuscula, textoDeError } from '../../agenda/logica';
import { primerNombre } from '../logica';

type Accion = ResolucionRecolocacion['action'];

interface Props extends PropsPantalla {
    recolocacion: Recolocacion;
    cliente: string;
    profesionales: readonly Profesional[];
    onCerrar: () => void;
    onHecho: () => void;
}

/**
 * Resolver una cita afectada (9.7, `resolveAppointmentRelocation`): pasarla al profesional libre que propone Citas (misma
 * hora; si el cliente eligió al suyo, llamarle antes, G438), darle otra hora con el mismo profesional (huecos de
 * `searchAppointmentSlots`), hablar antes con el cliente, mantenerla o cancelarla devolviendo todo. Se avisa al cliente
 * salvo al apuntar «hablar antes».
 */
export function Resolver({ ops, negocio, movil, membresia, recolocacion: r, cliente, profesionales, onCerrar, onHecho }: Props) {
    const avisar = useAvisoBreve();
    const hoy = hoyEn(membresia?.timezone);
    const a = r.appointment;
    const seg = a?.segments[0];
    const nombreDe = (id: string | null | undefined) => profesionales.find((p) => p.id === id)?.display_name ?? '';
    const pro = seg?.professional_display_name ?? nombreDe(seg?.professional_id);
    const primero = primerNombre(pro);
    const propuesto = r.proposed_professional_id == null ? null : nombreDe(r.proposed_professional_id);
    const [accion, setAccion] = useState<Accion | null>(propuesto === null ? null : 'relocate');
    const [dia, setDia] = useState(a?.local_date ?? hoy);
    const [horas, setHoras] = useState<string[] | null>(null);
    const [hora, setHora] = useState('');
    const [ocupado, setOcupado] = useState(false);

    // Los huecos libres del mismo profesional el día elegido (para «Darle otra hora»).
    useEffect(() => {
        if (accion !== 'move' || a === undefined) {
            return;
        }
        let vivo = true;
        ops.searchAppointmentSlots(negocio, {
            services: a.segments.map((s) => ({ service_id: s.service_id, answers: s.answers.map((x) => (x.option_id != null ? { question_id: x.question_id, option_ids: [x.option_id] } : x.number_value != null ? { question_id: x.question_id, number_value: x.number_value } : { question_id: x.question_id, text_value: x.text_value ?? '' })) })),
            ...(seg?.professional_id === undefined ? {} : { professional_id: seg.professional_id }),
            mode: 'list',
            from: dia,
            to: dia,
            exclude_appointment_id: a.id,
        })
            .then((h) => {
                if (vivo) {
                    setHoras(h.slots.map((s) => s.local_time));
                }
            })
            .catch(() => {
                if (vivo) {
                    setHoras([]);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, accion, dia, a, seg?.professional_id]);

    const opciones: [Accion, string, string, string, NombreIcono][] = [
        ...(propuesto === null
            ? []
            : ([['relocate', `Pasarla a ${propuesto}`, a?.professional_choice === 'any' ? 'Misma hora, con otra profesional que lo hace. Pidió «cualquiera».' : `Misma hora. Eligió a ${primero}: llámale antes.`, `Pasar a ${primerNombre(propuesto)}`, 'arrow-right']] as [Accion, string, string, string, NombreIcono][])),
        ['move', 'Darle otra hora', `Con ${primero}, en otro hueco libre. Se le avisa con la nueva hora.`, 'Mover y avisar', 'calendar-clock'],
        ['contact', 'Hablar antes con el cliente', 'Queda apuntado. La cita sigue en su hora hasta que lo resuelvas.', 'Apuntar', 'phone'],
        ['keep', 'Mantenerla', `${primero} la atenderá igualmente, aunque quede fuera de su horario.`, 'Mantener', 'check'],
        ['cancel', 'Cancelarla y devolver todo', 'Se le devuelve lo que pagó y se le avisa.', 'Cancelar la cita', 'calendar-x'],
    ];
    const actual = opciones.find((o) => o[0] === accion);

    const resolver = () => {
        if (accion === null || (accion === 'move' && hora === '')) {
            return;
        }
        const cuerpo: ResolucionRecolocacion = {
            action: accion,
            notify_customer: accion !== 'contact',
            ...(accion === 'relocate' && r.proposed_professional_id != null ? { professional_id: r.proposed_professional_id } : {}),
            ...(accion === 'move' ? { local_date: dia, local_time: hora } : {}),
        };
        setOcupado(true);
        ops.resolveAppointmentRelocation(negocio, r.id, cuerpo)
            .then(() => {
                const texto: Record<Accion, string> = {
                    relocate: `Cita de ${cliente} pasada a ${propuesto ?? 'otro profesional'} a la misma hora. Le hemos avisado.`,
                    move: `Cita de ${cliente} movida al ${fechaLarga(dia)} a las ${hora}. Le hemos avisado.`,
                    contact: `Apuntado: hay que hablar con ${cliente}. La cita sigue en su hora hasta que lo resuelvas.`,
                    keep: `La cita de ${cliente} se mantiene: ${pro} la atenderá.`,
                    cancel: `Cita de ${cliente} cancelada: se le devuelve todo y le hemos avisado.`,
                };
                avisar({ tipo: 'ok', texto: texto[accion] });
                onHecho();
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: textoDeError(e) }); })
            .finally(() => { setOcupado(false); });
    };

    return (
        <Ventana
            abierta
            titulo={`Cita de ${cliente}`}
            texto={a === undefined ? '' : `${mayuscula(fechaLarga(a.local_date))} a las ${a.local_time} · ${seg?.service_name ?? ''} con ${pro}.`}
            accion={actual?.[3] ?? 'Elige qué hacer'}
            accionIcono={actual?.[4] ?? 'check'}
            inactiva={actual === undefined || (accion === 'move' && hora === '')}
            ocupado={ocupado}
            ancho="520px"
            movil={movil}
            onAccion={resolver}
            onCerrar={onCerrar}
        >
            <p className="rv-sv-nota">
                <Icono nombre="user" />
                <span>{cliente}</span>
            </p>
            <div role="radiogroup" aria-label="Qué hacemos" className="rv-sv-grupo">
                {opciones.map(([clave, titulo, texto]) => (
                    <OpcionRadio key={clave} elegida={accion === clave} titulo={titulo} texto={texto} onElegir={() => { setAccion(clave); }} />
                ))}
            </div>
            {accion === 'move' && (
                <>
                    <Fecha etiqueta="Nueva fecha" valor={dia} min={hoy} onChange={(v) => { setDia(v); setHora(''); setHoras(null); }} />
                    <Desplegable
                        etiqueta="Hora"
                        valor={hora}
                        opciones={[{ valor: '', texto: horas === null ? 'Buscando huecos…' : horas.length === 0 ? 'Sin huecos libres ese día' : 'Elige la hora' }, ...(horas ?? []).map((h) => ({ valor: h, texto: h }))]}
                        ayuda="Huecos libres de este profesional."
                        onChange={setHora}
                    />
                </>
            )}
        </Ventana>
    );
}
